import React, { useState, useEffect } from "react";
import { StatsCards } from "@/components/reporting/StatsCards";
import { DateRangePicker } from "@/components/reporting/DateRangePicker";
import { RevenueChart } from "@/components/reporting/RevenueChart";
import { AttendanceChart } from "@/components/reporting/AttendanceChart";
import { EnrollmentChart } from "@/components/reporting/EnrollmentChart";
import { EnrollmentCapacityChart } from "@/components/reporting/EnrollmentCapacityChart";
import { StudentRetentionChart } from "@/components/reporting/StudentRetentionChart";
import { SchedulePerformanceCard } from "@/components/reporting/SchedulePerformanceCard";
import ReportExportPanel from "@/components/reporting/ReportExportPanel";
import { AIReportingChat } from "@/components/reporting/AIReportingChat";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { motion } from "framer-motion";
import { StandardPageHeader } from "@/components/layout/StandardPageHeader";
import { BarChart2, BookIcon, Bot, ExternalLink, TrendingUp } from "lucide-react";
import FinancialOverview from "@/components/dashboard/FinancialOverview";
import RevenueForecast from "@/components/reporting/RevenueForecast";
import MilestoneCelebration from "@/components/reporting/MilestoneCelebration";
import { useFinancialOverview } from "@/hooks/useFinancialOverview";
import { useNotify } from "@/hooks/useNotify";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { 
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
  }
};

const Reporting: React.FC = () => {
  const [dateRange, setDateRange] = useState<{
    from: Date;
    to: Date;
  }>({
    from: new Date(new Date().setMonth(new Date().getMonth() - 3)),
    to: new Date(),
  });

  const { data: financialData } = useFinancialOverview();
  const notify = useNotify();
  const [alerted, setAlerted] = useState({ large: false, overdue: false });

  useEffect(() => {
    if (!financialData) return;
    // Large payment alert (example: > $1000)
    const largePayment = financialData.topOutstandingInvoices?.find(inv => inv.amount >= 1000);
    if (largePayment && !alerted.large) {
      notify.success(`Large payment due: $${largePayment.amount.toLocaleString()} from ${largePayment.familyName}`, { duration: 7000 });
      setAlerted(a => ({ ...a, large: true }));
    }
    // Overdue payment alert
    if (financialData.overduePayments > 0 && !alerted.overdue) {
      notify.warning(`You have overdue payments totaling $${financialData.overduePayments.toLocaleString()}.`, { duration: 7000 });
      setAlerted(a => ({ ...a, overdue: true }));
    }
  }, [financialData, notify, alerted]);

  return (
    <motion.div 
      className="space-y-6 p-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={itemVariants}>
        <StandardPageHeader
          title="Reporting"
          subtitle="View and analyze your hive's performance data"
          icon={BarChart2}
        />
      </motion.div>

      <motion.div variants={itemVariants}>
        <Tabs defaultValue="standard" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="standard">
              <BookIcon className="h-4 w-4 mr-2" />
              Standard Reports
            </TabsTrigger>
            <TabsTrigger value="ai">
              <Bot className="h-4 w-4 mr-2" />
              AI-Powered Reports
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="standard">
            <div className="space-y-6">
              <motion.div variants={itemVariants}>
                <MilestoneCelebration />
              </motion.div>

              <motion.div variants={itemVariants}>
                <FinancialOverview />
              </motion.div>

              <motion.div variants={itemVariants}>
                <RevenueForecast />
              </motion.div>

              <motion.div variants={itemVariants} className="flex justify-end">
                <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
              </motion.div>

              <motion.div variants={itemVariants}>
                <StatsCards dateRange={dateRange} />
              </motion.div>

              <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <EnrollmentCapacityChart dateRange={dateRange} />
                <SchedulePerformanceCard dateRange={dateRange} />
              </motion.div>

              <motion.div variants={itemVariants}>
                <ReportExportPanel dateRange={dateRange} />
              </motion.div>

              <motion.div variants={itemVariants}>
                <Tabs defaultValue="revenue" className="w-full">
                  <TabsList className="mb-4">
                    <TabsTrigger value="revenue">Revenue</TabsTrigger>
                    <TabsTrigger value="attendance">Attendance</TabsTrigger>
                    <TabsTrigger value="enrollment">Enrollment</TabsTrigger>
                    <TabsTrigger value="retention">Student Retention</TabsTrigger>
                  </TabsList>
                  <TabsContent value="revenue">
                    <Card className="bg-white/50 backdrop-blur-sm">
                      <RevenueChart dateRange={dateRange} />
                    </Card>
                  </TabsContent>
                  <TabsContent value="attendance">
                    <Card className="bg-white/50 backdrop-blur-sm">
                      <AttendanceChart dateRange={dateRange} />
                    </Card>
                  </TabsContent>
                  <TabsContent value="enrollment">
                    <Card className="bg-white/50 backdrop-blur-sm">
                      <EnrollmentChart dateRange={dateRange} />
                    </Card>
                  </TabsContent>
                  <TabsContent value="retention">
                    <StudentRetentionChart dateRange={dateRange} />
                  </TabsContent>
                </Tabs>
              </motion.div>
            </div>
          </TabsContent>
          
          <TabsContent value="ai">
            <AIReportingChat />
          </TabsContent>
        </Tabs>
      </motion.div>
    </motion.div>
  );
};

export default Reporting;
